import { useEffect, useState } from 'react';
import { getApiBase } from '../lib/api';

interface UseCountApiToolStatsOptions {
  readonly toolSlug: string | null;
}

interface UseCountApiToolStatsResult {
  readonly total: number | null;
  readonly today: number | null;
  readonly loading: boolean;
  readonly error: string | null;
}

const getTodayKeyPart = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  (error as { name: string }).name === 'AbortError';

/**
 * 透過後端代理 CountAPI 取得工具的「今日使用次數」與「累計使用次數」。
 * - 每次載入工具頁會呼叫一次後端 API（後端再去打 CountAPI）。
 * - 主要用於顯示「已有 N 人使用」或「今日使用 N 次」等數字。
 */
export const useCountApiToolStats = ({
  toolSlug,
}: UseCountApiToolStatsOptions): UseCountApiToolStatsResult => {
  const [total, setTotal] = useState<number | null>(null);
  const [today, setToday] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!toolSlug) {
      return;
    }

    const abortController = new AbortController();
    let isCancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      const todayKeyPart = getTodayKeyPart();
      const apiBase = getApiBase();

      try {
        const url = `${apiBase}/api/public/tool-usage/${encodeURIComponent(
          toolSlug
        )}?_=${encodeURIComponent(todayKeyPart)}`;

        const response = await fetch(url, { signal: abortController.signal });

        if (!response.ok) {
          throw new Error(
            `Tool usage API request failed with status ${response.status}`
          );
        }

        const data = (await response.json()) as {
          total?: number;
          today?: number;
        };

        const totalValue =
          typeof data.total === 'number' && Number.isFinite(data.total)
            ? data.total
            : 0;
        const todayValue =
          typeof data.today === 'number' && Number.isFinite(data.today)
            ? data.today
            : 0;

        if (isCancelled) {
          return;
        }

        setTotal(totalValue);
        setToday(todayValue);
      } catch (err) {
        if (isCancelled || isAbortError(err)) {
          return;
        }

        setError('載入使用次數時發生錯誤，請稍後再試。');
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [toolSlug]);

  return { total, today, loading, error };
};
